import { useRef, type CSSProperties } from "react";

type PreviewCopy = {
  modalTitle: string;
  subtitle: string;
  servicesHeading: string;
  productsHeading: string;
  discountBadgeLabel: string;
  productAddLabel: string;
  productAddedLabel: string;
  productVariantLabel: string;
  productsPreviousLabel: string;
  productsNextLabel: string;
  closeLabel: string;
  selectedProductError: string;
  selectedItemsError: string;
  addFailedError: string;
  addedToCartLabel: string;
  bundleExpertBadgeLabel: string;
  bundleItemLabel: string;
  bundleItemDescription: string;
  bundleCompletePriceLabel: string;
  bundleSelectedPriceLabel: string;
  bundleCompleteLabel: string;
  bundleSelectedLabel: string;
  bundleAddingLabel: string;
  continueShoppingLabel: string;
  continueLabel: string;
  declineLabel: string;
};

type PreviewService = {
  id: string;
  title: string;
  description?: string;
  badge?: string;
  imageUrl?: string;
  price?: string;
  selectedByDefault?: boolean;
};

type PreviewProduct = {
  id: string;
  title: string;
  imageUrl?: string;
  price?: string;
  variantTitle?: string;
  bundleLabel?: string;
  bundleDescription?: string;
};

type PreviewDesign = {
  accentColor: string;
  buttonTextColor: string;
  modalBackgroundColor: string;
  textColor: string;
  layout: string;
  maxRecommendedProducts: number;
};

type LivePreviewProps = {
  copy?: PreviewCopy;
  services?: PreviewService[];
  products?: PreviewProduct[];
  design?: PreviewDesign;
  discount?: {
    enabled: boolean;
    percentage: number;
    countdownMinutes: number;
  };
  placement?: "pre_checkout" | "after_add" | "product_page";
  bundle?: {
    preselectAll: boolean;
    multiplyByTriggerQuantity: boolean;
  };
};

const fallbackCopy: PreviewCopy = {
  modalTitle: "Complete your cart",
  subtitle: "Add useful extras before checkout. Nothing is required.",
  servicesHeading: "Optional services",
  productsHeading: "Recommended products",
  discountBadgeLabel: "{percentage}% off",
  productAddLabel: "Add",
  productAddedLabel: "Added",
  productVariantLabel: "Choose variant",
  productsPreviousLabel: "Previous products",
  productsNextLabel: "Next products",
  closeLabel: "Close",
  selectedProductError: "The selected product could not be added to cart. Choose another product or continue without it.",
  selectedItemsError: "Some selected items could not be added to cart.",
  addFailedError: "Selected items could not be added to cart. Choose another product or continue without it.",
  addedToCartLabel: "Added to cart",
  bundleExpertBadgeLabel: "Recommended by our experts",
  bundleItemLabel: "Recommended addition",
  bundleItemDescription: "Complements your selected product.",
  bundleCompletePriceLabel: "Complete bundle price",
  bundleSelectedPriceLabel: "Selected products price",
  bundleCompleteLabel: "Add complete bundle",
  bundleSelectedLabel: "Add selected products",
  bundleAddingLabel: "Adding products...",
  continueShoppingLabel: "Continue without extras",
  continueLabel: "Continue to checkout",
  declineLabel: "Continue without extras",
};

const fallbackDesign: PreviewDesign = {
  accentColor: "#126249",
  buttonTextColor: "#ffffff",
  modalBackgroundColor: "#ffffff",
  textColor: "#202223",
  layout: "services_first",
  maxRecommendedProducts: 4,
};

const fallbackServices: PreviewService[] = [
  {
    id: "fallback-service",
    title: "Package protection",
    description: "Protect your delivery against loss or damage.",
    badge: "87% choose delivery protection",
    price: "$2.95",
    selectedByDefault: false,
  },
];

const fallbackProducts: PreviewProduct[] = [
  { id: "fallback-product-1", title: "Travel pouch", price: "$18.00", bundleLabel: "Useful accessory", bundleDescription: "Keeps the main product protected and ready to use." },
  { id: "fallback-product-2", title: "Care kit", price: "$12.00", bundleLabel: "Easy maintenance", bundleDescription: "Everything needed for regular care in one package." },
];

export function LivePreview({
  copy = fallbackCopy,
  services = fallbackServices,
  products = fallbackProducts,
  design = fallbackDesign,
  discount,
  placement = "pre_checkout",
  bundle = { preselectAll: false, multiplyByTriggerQuantity: false },
}: LivePreviewProps) {
  const productsRef = useRef<HTMLDivElement | null>(null);
  const visibleProducts = products.slice(0, design.maxRecommendedProducts || fallbackDesign.maxRecommendedProducts);
  const activeDiscount = discount?.enabled && discount.percentage > 0 ? discount : undefined;
  const isAfterAdd = placement === "after_add";
  const isProductPage = placement === "product_page";
  const style = {
    "--aiou-accent": design.accentColor || fallbackDesign.accentColor,
    "--aiou-button-text": design.buttonTextColor || fallbackDesign.buttonTextColor,
    "--aiou-bg": design.modalBackgroundColor || fallbackDesign.modalBackgroundColor,
    "--aiou-text": design.textColor || fallbackDesign.textColor,
  } as CSSProperties;

  function copyValue(field: keyof PreviewCopy) {
    return copy[field] || fallbackCopy[field];
  }

  function formatCopy(field: keyof PreviewCopy, values: Record<string, string | number>) {
    return copyValue(field).replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));
  }

  function scrollProducts(direction: -1 | 1) {
    productsRef.current?.scrollBy({ left: direction * Math.round(productsRef.current.clientWidth * 0.8), behavior: "smooth" });
  }

  function discountedPrice(price: string, percentage: number) {
    const match = price.match(/^(.*?)(\d+(?:[.,]\d+)?)(.*?)$/);
    if (!match) return "";
    const amount = Number(match[2].replace(",", "."));
    if (!Number.isFinite(amount)) return "";
    return `${match[1]}${Math.max(0, amount * (1 - percentage / 100)).toFixed(2)}${match[3]}`;
  }

  function renderProductPrice(price: string) {
    if (!activeDiscount) return <span className="aiou-product-price">{price}</span>;
    return (
      <span className="aiou-product-price">
        <span className="aiou-price-original">{price}</span>
        <span className="aiou-price-current">{discountedPrice(price, activeDiscount.percentage) || price}</span>
      </span>
    );
  }

  function previewPriceAmount(price: string) {
    const value = price.replace(/\s/g, "").replace(/[^\d,.-]/g, "").replace(",", ".");
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  function bundlePreviewTotal() {
    const selectedProducts = bundle.preselectAll ? visibleProducts : [];
    const total = selectedProducts.reduce((sum, product) => {
      const amount = previewPriceAmount(product.price || "$18.00");
      return sum + (activeDiscount ? amount * (1 - activeDiscount.percentage / 100) : amount);
    }, 0);
    const samplePrice = (visibleProducts[0]?.price || "$18.00").match(/^(.*?)(\d+(?:[.,]\d+)?)(.*?)$/);
    return `${samplePrice?.[1] ?? ""}${total.toFixed(2)}${samplePrice?.[3] ?? ""}`;
  }

  function renderBundleDialog(previewId: string, accessible: boolean, titleId: string) {
    const tabIndex = accessible ? undefined : -1;
    const complete = bundle.preselectAll && visibleProducts.length > 0;
    const primaryLabel = complete ? copyValue("bundleCompleteLabel") : copyValue("bundleSelectedLabel");
    const priceLabel = complete ? copyValue("bundleCompletePriceLabel") : copyValue("bundleSelectedPriceLabel");
    return (
      <div
        className="aiou-dialog aiou-preview-dialog aiou-dialog--bundle"
        data-layout="products_first"
        role={accessible ? "dialog" : undefined}
        aria-hidden={accessible ? undefined : "true"}
        aria-modal={accessible ? "true" : undefined}
        aria-labelledby={accessible ? titleId : undefined}
        style={style}
      >
        <button className="aiou-close" aria-label={accessible ? copyValue("closeLabel") : undefined} type="button" tabIndex={tabIndex}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
        <div className="aiou-bundle-header">
          <div className="aiou-added-confirmation">
            <span aria-hidden="true">✓</span>
            <strong>{copyValue("addedToCartLabel")}</strong>
          </div>
          <div className="aiou-bundle-recommendation">
            <span className="aiou-bundle-eyebrow">
              <span className="aiou-bundle-experts" aria-hidden="true">
                {[0, 1, 2].map((expert) => <span className="aiou-bundle-expert" key={expert}><span /></span>)}
              </span>
              {copyValue("bundleExpertBadgeLabel")}
            </span>
            <h2 id={titleId}>{copy.modalTitle || fallbackCopy.modalTitle}</h2>
            <p className="aiou-modal-subtitle">{copy.subtitle || fallbackCopy.subtitle}</p>
          </div>
        </div>
        <div className="aiou-body aiou-bundle-body">
          <section className="aiou-bundle-products">
            <div className="aiou-bundle-items">
              {visibleProducts.map((product, index) => (
                <article className={`aiou-bundle-item${bundle.preselectAll ? " is-selected" : ""}`} key={`${previewId}-${product.id}`}>
                  <label className="aiou-bundle-check-wrap">
                    <input
                      className="aiou-bundle-check"
                      type="checkbox"
                      aria-label={`${copyValue("productAddLabel")}: ${product.title}`}
                      defaultChecked={bundle.preselectAll}
                      tabIndex={tabIndex}
                    />
                    <span className="aiou-bundle-custom-check" aria-hidden="true" />
                  </label>
                  <div className={`aiou-bundle-item-image${product.imageUrl ? "" : " aiou-bundle-item-image--empty"}`}>
                    {product.imageUrl ? <img alt="" src={product.imageUrl} /> : null}
                  </div>
                  <div className="aiou-bundle-item-copy">
                    <span className="aiou-bundle-item-kicker">
                      {product.bundleLabel || copyValue("bundleItemLabel")}
                    </span>
                    <strong>{product.title || "Selected product"}</strong>
                    <p>{product.bundleDescription || copyValue("bundleItemDescription")}</p>
                    {product.variantTitle ? (
                      <select aria-label={`${copyValue("productVariantLabel")}: ${product.title}`} defaultValue={product.variantTitle} tabIndex={tabIndex}>
                        <option value={product.variantTitle}>{product.variantTitle}</option>
                      </select>
                    ) : null}
                  </div>
                  <div className="aiou-bundle-item-price">{renderProductPrice(product.price || "$18.00")}</div>
                </article>
              ))}
            </div>
          </section>
        </div>
        <div className="aiou-footer aiou-bundle-footer">
          <div className="aiou-bundle-summary">
            <span className="aiou-bundle-summary-label">{priceLabel}</span>
            <strong className="aiou-bundle-summary-total">{bundlePreviewTotal()}</strong>
          </div>
          <button className="aiou-primary" type="button" tabIndex={tabIndex}>
            <span>{primaryLabel}</span><span aria-hidden="true">→</span>
          </button>
          <button className="aiou-secondary" type="button" tabIndex={tabIndex}>{copyValue("continueShoppingLabel")}</button>
          <div className="aiou-error" role={accessible ? "alert" : undefined} />
        </div>
      </div>
    );
  }

  function renderSections(previewId: string, tabIndex?: number) {
    const serviceSection = services.length ? (
      <section key={`${previewId}-services`}>
        <h3 className="aiou-section-title">{copy.servicesHeading || fallbackCopy.servicesHeading}</h3>
        {services.map((service) => (
          <label className="aiou-service" key={`${previewId}-${service.id}`}>
            <input type="checkbox" defaultChecked={service.selectedByDefault} tabIndex={tabIndex} />
            <div className="aiou-service-media" data-empty={service.imageUrl ? undefined : "true"}>
              {service.imageUrl ? <img alt="" src={service.imageUrl} /> : null}
            </div>
            <span>
              {service.badge ? <em>{service.badge}</em> : null}
              <strong>{service.title || "Service"}</strong>
              <small>{service.description}</small>
            </span>
            <b>{service.price || "$2.95"}</b>
          </label>
        ))}
      </section>
    ) : null;

    const productSection = visibleProducts.length ? (
      <section key={`${previewId}-products`}>
        <h3 className="aiou-section-title">
          {copyValue("productsHeading")}
          {activeDiscount ? ` - ${activeDiscount.countdownMinutes}:00` : ""}
        </h3>
        <div className="aiou-products-shell" data-scrollable={visibleProducts.length > 3 ? "true" : "false"}>
          <button
            className="aiou-carousel-button aiou-carousel-prev"
            type="button"
            aria-label={copyValue("productsPreviousLabel")}
            tabIndex={tabIndex}
            onClick={() => scrollProducts(-1)}
          >
            ‹
          </button>
          <div className="aiou-products" ref={productsRef}>
            {visibleProducts.map((product) => (
              <article className="aiou-product" key={`${previewId}-${product.id}`}>
                {product.imageUrl ? <img alt="" src={product.imageUrl} /> : <div className="aiou-product-image" />}
                <div className="aiou-product-copy">
                  <strong>{product.title || "Selected product"}</strong>
                  {renderProductPrice(product.price || "$18.00")}
                </div>
                {activeDiscount ? <div className="aiou-product-discount">{formatCopy("discountBadgeLabel", { percentage: activeDiscount.percentage })}</div> : null}
                {product.variantTitle ? (
                  <select aria-label={copyValue("productVariantLabel")} defaultValue={product.variantTitle} tabIndex={tabIndex}>
                    <option value={product.variantTitle}>{product.variantTitle}</option>
                  </select>
                ) : null}
                <button type="button" aria-pressed={isAfterAdd && bundle.preselectAll ? "true" : "false"} tabIndex={tabIndex}>
                  {isAfterAdd && bundle.preselectAll ? copyValue("productAddedLabel") : copyValue("productAddLabel")}
                </button>
              </article>
            ))}
          </div>
          <button
            className="aiou-carousel-button aiou-carousel-next"
            type="button"
            aria-label={copyValue("productsNextLabel")}
            tabIndex={tabIndex}
            onClick={() => scrollProducts(1)}
          >
            ›
          </button>
        </div>
      </section>
    ) : null;

    return design.layout === "products_first" ? [productSection, serviceSection] : [serviceSection, productSection];
  }

  function renderDialog(previewId: string, accessible: boolean) {
    const titleId = `preview-title-${previewId}`;
    if (isAfterAdd) return renderBundleDialog(previewId, accessible, titleId);
    return (
      <div
        className="aiou-dialog aiou-preview-dialog"
        data-layout={design.layout || fallbackDesign.layout}
        role={accessible ? "dialog" : undefined}
        aria-hidden={accessible ? undefined : "true"}
        aria-modal={accessible ? "true" : undefined}
        aria-labelledby={accessible ? titleId : undefined}
        style={style}
      >
        <div className="aiou-head">
          <button className="aiou-close" aria-label={accessible ? copyValue("closeLabel") : undefined} type="button" tabIndex={accessible ? undefined : -1}>
            x
          </button>
          <h2 id={titleId}>{copy.modalTitle || fallbackCopy.modalTitle}</h2>
          <p>{copy.subtitle || fallbackCopy.subtitle}</p>
        </div>
        <div className="aiou-body">{renderSections(previewId, accessible ? undefined : -1)}</div>
        <div className="aiou-footer">
          <div className="aiou-error" role={accessible ? "status" : undefined} />
          <button className="aiou-primary" type="button" tabIndex={accessible ? undefined : -1}>
            {copy.continueLabel || fallbackCopy.continueLabel}
          </button>
          <button className="aiou-secondary" type="button" tabIndex={accessible ? undefined : -1}>
            {copy.declineLabel || fallbackCopy.declineLabel}
          </button>
        </div>
      </div>
    );
  }

  function renderProductPageBlock(previewId: string) {
    return (
      <section className="aiou-inline-preview" style={style} aria-labelledby={`inline-preview-title-${previewId}`}>
        <span className="aiou-inline-kicker">{copy.bundleExpertBadgeLabel || fallbackCopy.bundleExpertBadgeLabel}</span>
        <h2 id={`inline-preview-title-${previewId}`}>{copy.modalTitle || copy.productsHeading || fallbackCopy.productsHeading}</h2>
        <p>{copy.subtitle || fallbackCopy.subtitle}</p>
        <div className="aiou-inline-products">
          {visibleProducts.map((product) => (
            <label className="aiou-inline-product" key={`${previewId}-${product.id}`}>
              <input type="checkbox" defaultChecked={bundle.preselectAll} />
              <span className="aiou-inline-check" aria-hidden="true" />
              <span className={`aiou-inline-image${product.imageUrl ? "" : " is-empty"}`}>
                {product.imageUrl ? <img alt="" src={product.imageUrl} /> : null}
              </span>
              <span className="aiou-inline-copy">
                <small>{product.bundleLabel || copyValue("bundleItemLabel")}</small>
                <strong>{product.title || "Selected product"}</strong>
                <span>{product.bundleDescription || copyValue("bundleItemDescription")}</span>
              </span>
              {renderProductPrice(product.price || "$18.00")}
            </label>
          ))}
        </div>
        <button className="aiou-inline-primary" type="button">{copyValue("bundleSelectedLabel")}</button>
        <div className="aiou-inline-status" aria-live="polite" />
      </section>
    );
  }

  return (
    <div className="preview-surface" aria-label={isProductPage ? "Product page block live preview" : "Modal live preview"}>
      <div className="preview-devices">
        <div className="preview-device preview-device-desktop">
          {isProductPage ? renderProductPageBlock("preview") : renderDialog("preview", true)}
        </div>
      </div>
    </div>
  );
}
